import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getKiinteistoSasToken } from '../../../api/api';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';
import ImageBlock from './ImageBlock.jsx';
import config from '../../../devprodConfig';

const API_URL = config.apiBaseUrl;

// Two sizes only: base 0.875rem, meta 0.75rem
const S = {
  base: '0.875rem',
  meta: '0.75rem',
};

const LIITE_IMAGE_DEFAULTS = { category: 'Liitteet', pdfLayout: 'liite' };

function LabelInput({ value, onCommit }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <input
      type="text"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => onCommit(local)}
      placeholder="Liitteen otsikko PDF:ssä…"
      style={{
        width: '100%',
        fontSize: S.base,
        border: '1px solid #dee2e6',
        borderRadius: 4,
        padding: '4px 8px',
        color: '#495057',
        background: '#fff',
        outline: 'none',
      }}
    />
  );
}

function SpFileBrowser({ card, onAttach, alreadyAttached }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const uploadRef = useRef();

  const load = async (folderId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/sharepoint/files`, {
        params: folderId ? { folderId } : {},
        withCredentials: true,
      });
      setFiles(res.data || []);
    } catch {
      setError('Tiedostojen haku epäonnistui.');
    } finally {
      setLoading(false);
    }
  };

  const openRoot = async () => {
    if (files !== null) { setFiles(null); setFolderStack([]); return; }
    setLoading(true);
    setError(null);
    try {
      const osoite = card?.rakennukset_fulls?.[0]?.osoite;
      const postinumero = card?.rakennukset_fulls?.[0]?.postinumero;
      const kaupunki = card?.rakennukset_fulls?.[0]?.toimipaikka;
      const folderRes = await axios.get(`${API_URL}/sharepoint/folder/by-kiinteisto`, {
        params: { osoite, postinumero, kaupunki },
        withCredentials: true,
      });
      const folder = folderRes.data;
      if (folder?.id) {
        const rootStack = [{ id: folder.id, name: folder.name || 'Kohde' }];
        const rootFilesRes = await axios.get(`${API_URL}/sharepoint/files`, {
          params: { folderId: folder.id },
          withCredentials: true,
        });
        const rootFiles = rootFilesRes.data || [];
        const kohdetiedot = rootFiles.find(f => f.folder && /kohdetiedot/i.test(f.name));
        if (kohdetiedot) {
          const subRes = await axios.get(`${API_URL}/sharepoint/files`, {
            params: { folderId: kohdetiedot.id },
            withCredentials: true,
          });
          setFolderStack([...rootStack, { id: kohdetiedot.id, name: kohdetiedot.name }]);
          setFiles(subRes.data || []);
        } else {
          setFolderStack(rootStack);
          setFiles(rootFiles);
        }
      } else {
        setError('Kohdetta ei löydy SharePointista.');
        setFiles([]);
      }
    } catch {
      setError('SharePoint-yhteyttä ei saatavilla.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const openFolder = async (f) => {
    setFolderStack(prev => [...prev, { id: f.id, name: f.name }]);
    await load(f.id);
  };

  const goBack = async () => {
    const next = folderStack.slice(0, -1);
    setFolderStack(next);
    await load(next.length > 0 ? next[next.length - 1].id : null);
  };

  const uploadFiles = async (selectedFiles) => {
    const currentFolderId = folderStack[folderStack.length - 1]?.id;
    if (!currentFolderId) return;
    setUploading(true);
    try {
      for (const file of Array.from(selectedFiles)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderId', currentFolderId);
        await fetch(`${API_URL}/sharepoint/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
      }
      await load(currentFolderId);
    } catch {
      setError('Tiedoston lataus epäonnistui.');
    } finally {
      setUploading(false);
    }
  };

  const isPdf = (f) => !f.folder && f.name?.toLowerCase().endsWith('.pdf');
  const isAttached = (f) => alreadyAttached.some(a => a.fileId === f.id);

  const spOpen = files !== null;

  return (
    <div>
      <div
        role="button"
        onClick={!(loading && !spOpen) ? openRoot : undefined}
        style={{
          border: `1.5px dashed ${spOpen ? '#04aa00' : '#adb5bd'}`,
          borderRadius: 6,
          padding: '14px 12px',
          textAlign: 'center',
          cursor: loading && !spOpen ? 'default' : 'pointer',
          background: spOpen ? 'rgba(4,170,0,0.05)' : 'transparent',
          transition: 'border-color 0.15s, background 0.15s',
          fontSize: S.base,
          color: spOpen ? '#04aa00' : '#6c757d',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        {loading && !spOpen
          ? <span className="spinner-border spinner-border-sm" />
          : <i className="bi bi-microsoft" />}
        <span>
          {spOpen ? 'Sulje SharePoint-selain' : 'Selaa SharePoint-tiedostoja'}
        </span>
        {spOpen && <i className="bi bi-chevron-up" />}
      </div>

      {files !== null && (
        <div className="border rounded mt-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
          <div className="d-flex align-items-center gap-1 px-2 py-1 bg-light border-bottom" style={{ fontSize: S.meta }}>
            {folderStack.length > 1 && (
              <button type="button" className="btn btn-link btn-sm p-0 me-1" onClick={goBack}>
                <i className="bi bi-arrow-left" />
              </button>
            )}
            <div className="flex-grow-1 d-flex align-items-center gap-1 overflow-hidden">
              {folderStack.map((f, i) => (
                <span key={f.id} className="text-muted text-truncate">
                  {i > 0 && <span className="mx-1">/</span>}
                  <span className={i === folderStack.length - 1 ? 'text-dark fw-medium' : ''}>{f.name}</span>
                </span>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary flex-shrink-0 ms-auto d-flex align-items-center gap-1"
              style={{ fontSize: S.meta, padding: '2px 8px' }}
              onClick={() => uploadRef.current?.click()}
              disabled={uploading || !folderStack.length}
              title="Lataa tiedostoja tähän kansioon"
            >
              {uploading
                ? <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} />
                : <i className="bi bi-upload" />}
              Lataa
            </button>
            <input
              ref={uploadRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {error && <div className="px-3 py-2 text-danger" style={{ fontSize: S.meta }}>{error}</div>}

          {loading ? (
            <div className="text-center py-3"><span className="spinner-border spinner-border-sm text-secondary" /></div>
          ) : files.length === 0 ? (
            <div className="px-3 py-2 text-muted" style={{ fontSize: S.meta }}>Tyhjä kansio</div>
          ) : (
            files.map(f => {
              const isDir = !!f.folder;
              const pdf = isPdf(f);
              const attached = isAttached(f);
              return (
                <div
                  key={f.id}
                  className="d-flex align-items-center gap-2 px-3 py-2"
                  style={{
                    borderTop: '1px solid #f0f0f0',
                    opacity: !isDir && !pdf ? 0.4 : 1,
                    cursor: isDir || pdf ? 'pointer' : 'default',
                    backgroundColor: attached ? '#f0faf4' : undefined,
                    fontSize: S.base,
                  }}
                  onClick={() => isDir ? openFolder(f) : undefined}
                >
                  <i className={`bi flex-shrink-0 ${isDir ? 'bi-folder-fill text-warning' : pdf ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark text-secondary'}`} />
                  <span className="flex-grow-1 text-truncate">{f.name}</span>
                  {f.size && !isDir && (
                    <span className="text-muted flex-shrink-0" style={{ fontSize: S.meta }}>
                      {Math.round(f.size / 1024)} KB
                    </span>
                  )}
                  {pdf && (
                    <button
                      type="button"
                      className={`btn btn-sm flex-shrink-0 ${attached ? 'btn-success' : 'btn-outline-success'}`}
                      style={{ fontSize: S.meta, padding: '2px 10px' }}
                      onClick={e => { e.stopPropagation(); onAttach(f); }}
                    >
                      {attached ? <><i className="bi bi-check me-1" />Lisätty</> : 'Lisää'}
                    </button>
                  )}
                  {isDir && <i className="bi bi-chevron-right text-muted flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function PdfAttachmentBlock({ data, onChange }) {
  const { rakennusData, mergedRakennusData } = useReportTemplateContext();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const attachments = data?.attachments || [];
  const card = rakennusData || mergedRakennusData;

  const liiteImageData = { ...LIITE_IMAGE_DEFAULTS, ...(data?.liiteImages || {}) };
  const handleLiiteImageChange = (newData) => onChange({ ...data, liiteImages: newData });

  const attachSharePoint = (f) => {
    if (attachments.some(a => a.fileId === f.id)) {
      onChange({ ...data, attachments: attachments.filter(a => a.fileId !== f.id) });
      return;
    }
    onChange({
      ...data,
      attachments: [...attachments, {
        source: 'sharepoint',
        fileId: f.id,
        name: f.name,
        downloadUrl: f['@microsoft.graph.downloadUrl'] || null,
        webUrl: f.webUrl || null,
      }],
    });
  };

  const uploadLocal = async (files) => {
    const kiinteistoId = card?.id_kiinteisto || 'global';
    setUploading(true);
    try {
      const added = [];
      for (const file of Array.from(files)) {
        if (!file.name.toLowerCase().endsWith('.pdf')) continue;
        const blobName = `pdf-attachments/${kiinteistoId}/${crypto.randomUUID()}-${file.name.replace(/\s+/g, '_')}`;
        const { data: tokenData } = await getKiinteistoSasToken(blobName);
        await fetch(tokenData.sasUrl, {
          method: 'PUT',
          body: file,
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/pdf' },
        });
        added.push({ source: 'local', azure_blob_name: blobName, name: file.name, sasUrl: tokenData.sasUrl });
      }
      if (added.length) onChange({ ...data, attachments: [...attachments, ...added] });
    } catch (err) {
      console.error('PDF upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => onChange({ ...data, attachments: attachments.filter((_, i) => i !== idx) });
  const setLabel = (idx, label) => {
    onChange({ ...data, attachments: attachments.map((a, i) => i === idx ? { ...a, label } : a) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Attached PDF files */}
      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {attachments.map((att, i) => (
            <div key={i} style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i
                  className={`bi flex-shrink-0 ${att.source === 'sharepoint' ? 'bi-microsoft text-primary' : 'bi-file-earmark-pdf text-danger'}`}
                  style={{ fontSize: S.base }}
                />
                <span style={{ flexGrow: 1, fontSize: S.base, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={att.name}>
                  {att.name}
                </span>
                <span style={{ fontSize: S.meta, color: '#6c757d', flexShrink: 0 }}>
                  {att.source === 'sharepoint' ? 'SharePoint' : 'Paikallinen'}
                </span>
                <button
                  type="button"
                  className="btn btn-link text-danger p-0 flex-shrink-0"
                  style={{ fontSize: S.base, lineHeight: 1 }}
                  onClick={() => remove(i)}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              <LabelInput value={att.label ?? ''} onCommit={label => setLabel(i, label)} />
            </div>
          ))}
        </div>
      )}

      {/* Add from SharePoint */}
      <SpFileBrowser
        card={card}
        onAttach={attachSharePoint}
        alreadyAttached={attachments.filter(a => a.source === 'sharepoint')}
      />

      {/* Add from local file — always visible drop zone */}
      <div
        style={{
          border: `1.5px dashed ${dragOver ? '#0d6efd' : '#adb5bd'}`,
          borderRadius: 6,
          padding: '14px 12px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'rgba(13,110,253,0.05)' : 'transparent',
          transition: 'border-color 0.15s, background 0.15s',
          fontSize: S.base,
          color: '#6c757d',
        }}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); uploadLocal(e.dataTransfer.files); }}
      >
        {uploading
          ? <><span className="spinner-border spinner-border-sm me-2" />Ladataan…</>
          : <><i className="bi bi-file-earmark-pdf me-2" />Raahaa PDF tähän tai <span style={{ color: '#0d6efd', textDecoration: 'underline' }}>selaa tiedostoja</span></>
        }
      </div>
      <input ref={fileRef} type="file" accept=".pdf" multiple style={{ display: 'none' }}
        onChange={e => { uploadLocal(e.target.files); e.target.value = ''; }} />

      {/* Liitteet images via ImageBlock */}
      <ImageBlock
        data={liiteImageData}
        onChange={handleLiiteImageChange}
        mode="report"
      />

    </div>
  );
}
